import { useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { getFieldErrors } from '@/services/api';
import { PlanCreatedNotAttachedError, type useProductPlanMutations } from './useProductPlanMutations';
import type { PlanFormInput, PlanFormOutput } from '../schemas/plan.schema';

type ProductPlanMutations = ReturnType<typeof useProductPlanMutations>;

export function useCreatePlanForProduct(mutations: ProductPlanMutations, onAttached?: () => void) {
    const { createAndAttachMutation } = mutations;
    const [isOpen, setIsOpen] = useState(false);
    const [warning, setWarning] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const setFieldErrorRef = useRef<UseFormSetError<PlanFormInput> | null>(null);

    const open = () => {
        setWarning(null);
        setError(null);
        setIsOpen(true);
    };

    const close = () => setIsOpen(false);

    const submit = (data: PlanFormOutput) => {
        setError(null);
        createAndAttachMutation.mutate(data, {
            onSuccess: () => {
                setIsOpen(false);
                onAttached?.();
            },
            onError: (cause: unknown) => {
                if (cause instanceof PlanCreatedNotAttachedError) {
                    setIsOpen(false);
                    setWarning(`"${cause.plan.name}" was created but not attached. Search for it above to attach it.`);
                    return;
                }
                const fieldErrors = getFieldErrors(cause);
                Object.entries(fieldErrors).forEach(([field, messages]) => {
                    setFieldErrorRef.current?.(field as keyof PlanFormInput, { message: messages[0] });
                });
                if (Object.keys(fieldErrors).length === 0) setError('Failed to create plan.');
            },
        });
    };

    return {
        isOpen,
        open,
        close,
        submit,
        warning,
        error,
        isPending: createAndAttachMutation.isPending,
        onSetErrorRef: (fn: UseFormSetError<PlanFormInput>) => { setFieldErrorRef.current = fn; },
    };
}
